import { useState } from "react";
import { createPortal } from "react-dom";
import SearchSelect from "./SearchSelect";

/* Reusable bulk-status-change confirmation dialog. Mirrors the shape of
 * BulkDeleteDialog so the two modal experiences feel consistent.
 *
 * Props:
 *  - count: number of records currently selected
 *  - lockedRecords: optional array of records that have payments applied
 *      and therefore cannot be moved to any status OTHER than "paid".
 *      Each item should have { id, label }. The dialog still allows the
 *      change to "paid" when the user picks that status.
 *  - statuses: array of { value, label } for the picker — the module
 *      provides its own status set (Shipments: draft/received/billed,
 *      Bills: draft/finalized/paid, Invoices: draft/sent/paid, Expense
 *      Bills: draft/pending/paid).
 *  - onConfirm(newStatus): called when user confirms; receives the
 *      chosen status string. Caller filters out locked records when
 *      newStatus !== "paid".
 *  - onCancel
 *  - lang: "en" | "es"
 */

var T = {
  en: {
    title: "Change status",
    pick: "Pick new status",
    msg: "Change {count} selected record{s} to ",
    lockedTitle: "Some records have payments",
    lockedHint: "Records with payments can only be moved to Paid. They will be skipped if you pick a different status.",
    cancel: "Cancel",
    confirm: "Apply",
    nothingToChange: "All selected records have payments — pick Paid or cancel.",
  },
  es: {
    title: "Cambiar estado",
    pick: "Elige el nuevo estado",
    msg: "¿Cambiar {count} registro{s} a ",
    lockedTitle: "Algunos registros tienen pagos",
    lockedHint: "Los registros con pagos solo pueden cambiarse a Pagado. Se omitirán si eliges otro estado.",
    cancel: "Cancelar",
    confirm: "Aplicar",
    nothingToChange: "Todos los registros seleccionados tienen pagos — elige Pagado o cancela.",
  },
};

export default function BulkStatusDialog({ count, lockedRecords, statuses, onConfirm, onCancel, lang }) {
  var t = T[lang || "es"];
  var locked = lockedRecords || [];
  var [pick, setPick] = useState("");
  var pickIsPaid = pick === "paid";
  /* When the target is "paid", every selected record (even locked ones)
     can be moved. Otherwise locked records are excluded from the count
     and we surface the warning. */
  var applicableCount = pickIsPaid ? count : Math.max(0, count - locked.length);
  var canApply = !!pick && applicableCount > 0;
  var pickedLabel = "";
  if (pick) {
    var opt = (statuses || []).find(function (s) { return s.value === pick; });
    pickedLabel = opt ? opt.label : pick;
  }

  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}
    >
      <div
        onClick={function (e) { e.stopPropagation(); }}
        style={{ background: "var(--color-bg-card-solid)", color: "var(--color-text)", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 22 }}>{"🔁"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--color-accent)" }}>{t.title}</h3>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>{t.pick}</label>
          <SearchSelect
            value={pick}
            onChange={function (v) { setPick(v); }}
            placeholder={t.pick + "…"}
            options={statuses || []}
          />
        </div>

        {pick && (
          <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5 }}>
            {t.msg.replace("{count}", String(applicableCount)).replace("{s}", applicableCount === 1 ? "" : "s")}
            <strong>{pickedLabel}</strong>?
          </p>
        )}

        {locked.length > 0 && pick && !pickIsPaid && (
          <div style={{ marginTop: 4, marginBottom: 14, padding: 12, borderRadius: 8, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#B45309", marginBottom: 6 }}>{t.lockedTitle}</div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 8 }}>{t.lockedHint}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--color-text-secondary)", maxHeight: 160, overflowY: "auto" }}>
              {locked.map(function (r, i) {
                return <li key={i} style={{ fontFamily: "'JetBrains Mono', monospace", marginBottom: 2 }}>{r.label || r.id}</li>;
              })}
            </ul>
            {applicableCount === 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#B45309", fontStyle: "italic" }}>{t.nothingToChange}</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <button
            onClick={onCancel}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
          >
            {t.cancel}
          </button>
          <button
            onClick={canApply ? function () { onConfirm(pick); } : undefined}
            disabled={!canApply}
            style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: canApply ? "var(--color-accent)" : "var(--color-border)", color: "var(--color-accent-text)", cursor: canApply ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700, opacity: canApply ? 1 : 0.6 }}
          >
            {t.confirm}{canApply ? " (" + applicableCount + ")" : ""}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
